import { useMemo } from 'react'

import type { CompanyFeatures, CompanyFeaturesSchemaFragment } from 'gql/__generated__/graphql'

import { DEFAULT_COMPANY_FEATURES } from './types'

/**
 * Hook to get company features with defaults applied
 * @param features - Raw features from GraphQL query (may be null/undefined)
 * @returns Company features with defaults for missing values
 */
export const useCompanyFeatures = (
  features: CompanyFeaturesSchemaFragment | null | undefined,
): CompanyFeatures => {
  return useMemo(() => {
    if (!features) {
      return DEFAULT_COMPANY_FEATURES
    }

    return {
      ANALYTICS_ENABLED: features.ANALYTICS_ENABLED ?? DEFAULT_COMPANY_FEATURES.ANALYTICS_ENABLED,
      DOC_TO_COURSE_ENABLED:
        features.DOC_TO_COURSE_ENABLED ?? DEFAULT_COMPANY_FEATURES.DOC_TO_COURSE_ENABLED,
      DOC_TO_COURSE_MONTHLY_LIMIT:
        features.DOC_TO_COURSE_MONTHLY_LIMIT ??
        DEFAULT_COMPANY_FEATURES.DOC_TO_COURSE_MONTHLY_LIMIT,
      DOC_TO_COURSE_WITHOUT_AI_ENABLED:
        features.DOC_TO_COURSE_WITHOUT_AI_ENABLED ??
        DEFAULT_COMPANY_FEATURES.DOC_TO_COURSE_WITHOUT_AI_ENABLED,
      DAM_ENABLED: features.DAM_ENABLED ?? DEFAULT_COMPANY_FEATURES.DAM_ENABLED,
      DYNAMIC_SCORM_ENABLED:
        features.DYNAMIC_SCORM_ENABLED ?? DEFAULT_COMPANY_FEATURES.DYNAMIC_SCORM_ENABLED,
      CONTENT_SPACE_ENABLED:
        features.CONTENT_SPACE_ENABLED ?? DEFAULT_COMPANY_FEATURES.CONTENT_SPACE_ENABLED,
    }
  }, [features])
}

/**
 * Hook to check if a specific feature is enabled
 * @param features - Raw features from GraphQL query
 * @param key - Feature key to check
 * @returns boolean indicating if feature is enabled
 */
export const useIsFeatureEnabled = (
  features: CompanyFeaturesSchemaFragment | null | undefined,
  key: keyof CompanyFeatures,
): boolean => {
  const allFeatures = useCompanyFeatures(features)
  return Boolean(allFeatures[key])
}

/**
 * Hook to check if Doc to Course (AI Import) is available
 * @param features - Raw features from GraphQL query
 * @param usedAiImports - Number of AI imports used this month
 * @returns Object with availability info
 */
export const useDocToCourseAvailability = (
  features: CompanyFeaturesSchemaFragment | null | undefined,
  usedAiImports: number | null | undefined,
) => {
  const allFeatures = useCompanyFeatures(features)

  return useMemo(() => {
    const isEnabled = allFeatures.DOC_TO_COURSE_ENABLED
    const limit = allFeatures.DOC_TO_COURSE_MONTHLY_LIMIT
    const used = usedAiImports ?? 0
    const remaining = Math.max(0, limit - used)
    const isWithinLimit = used < limit

    return {
      isEnabled,
      isAvailable: isEnabled && isWithinLimit,
      limit,
      used,
      remaining,
      isWithinLimit,
      isWithoutAiEnabled: allFeatures.DOC_TO_COURSE_WITHOUT_AI_ENABLED,
    }
  }, [allFeatures, usedAiImports])
}
